import { useCallback, useMemo } from "react";
import type { Dispatch } from "redux";

import {
  getIsColumn,
  getIsTab,
  getWidget,
} from "legacy/selectors/entitiesSelector";
import { selectWidgetDisplayName } from "legacy/selectors/sagaSelectors";
import { useAppSelector } from "store/helpers";
import { getEventById } from "store/slices/application/events/selectors";
import { getStateVarById } from "store/slices/application/stateVars/selectors";
import { getTimerById } from "store/slices/application/timers/selectors";
import type { AppState } from "store/types";

import { ItemTypeNonWidget } from "../ItemKindConstants";
import type { ItemKinds } from "../ItemKindConstants";
import type { ItemKindAccessor } from "../ItemKinds";

export const AiHistoryAccessor: ItemKindAccessor<ItemKinds.AI_EDITS_HISTORY> = {
  useItemName: (itemProperties: any) => {
    const displayName = useAppSelector(
      useCallback(
        (state: AppState) =>
          selectWidgetDisplayName(state, itemProperties.widgetId),
        [itemProperties.widgetId],
      ),
    );
    const isTab = useAppSelector(
      useCallback(
        (state: AppState) => getIsTab(state, itemProperties.widgetId),
        [itemProperties.widgetId],
      ),
    );
    const isColumn = useAppSelector(
      useCallback(
        (state: AppState) => getIsColumn(state, itemProperties.widgetId),
        [itemProperties.widgetId],
      ),
    );
    return useMemo(
      () => ({
        name: itemProperties.widgetName,
        displayName,
        editable: !isColumn,
        requiresValidation: !isTab,
      }),
      [displayName, isColumn, isTab, itemProperties.widgetName],
    );
  },
  itemType: () => ItemTypeNonWidget.AI_EDITS_HISTORY,
  useItemProperties: (itemId: string) => {
    const [historyIndex, itemType, entityId] = itemId.split(":");
    const widget = useAppSelector((state) => getWidget(state, entityId));
    const stateVar = useAppSelector((state) =>
      getStateVarById(state, entityId),
    );
    const timer = useAppSelector((state) => getTimerById(state, entityId));
    const event = useAppSelector((state) => getEventById(state, entityId));
    const resultsHistory = useAppSelector((state) => state.ai.resultsHistory);
    const itemFromHistory =
      resultsHistory[parseInt(historyIndex, 10)]?.entitiesState?.[entityId];
    if (!entityId) {
      return {};
    }
    if (itemType === "widget") {
      return {
        ...widget,
        ...itemFromHistory,
      };
    } else if (itemType === "stateVariable") {
      const combinedStateVar = {
        ...stateVar,
        ...itemFromHistory,
      };
      return combinedStateVar;
    } else if (itemType === "timer") {
      return {
        ...timer,
        ...itemFromHistory,
      };
    } else if (itemType === "event") {
      return {
        ...event,
        ...itemFromHistory,
      };
    }
    return {};
  },

  updateItemProperties: (
    dispatch: Dispatch<any>,
    properties: any,
    updates: Record<string, unknown>,
  ) => {
    // non-editable
  },
  deleteItemProperties: (
    dispatch: Dispatch<any>,
    properties: any,
    propertyPaths: string[],
  ) => {
    // non-editable
  },
  deleteItem: (dispatch, widgetId, _itemScope, _deleteAllSelected) => {
    // non-editable
  },
  icon: () => null,
};
